/* eslint-disable react/no-unstable-nested-components */
import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useMessage, useToastNotification } from '@/hooks';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dialog } from 'primereact/dialog';
import { Nullable } from 'primereact/ts-helpers';
import { ColumnProps } from 'primereact/column';
import { Messages } from 'primereact/messages';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCalculator } from '@fortawesome/free-solid-svg-icons';
import InfoButton from '@/components/InfoAlertActions/InfoAlertActions';

import {
  InputDateField,
  InputTextField,
  Button,
  Table,
  LoadingSpinner,
} from '@/components';
import { DataBl } from '@/types/containersBLType';
import { DataLiquidateContainerBL } from '@/types/liquidateContainerBLType';
import { queryClient } from '@/providers/AppProvider';
import { addContainerBlImport } from '../Depo20/services/addContainerBlImport';
import {
  saveAndAddSettlement,
  SaveSettlementValidated,
} from '../Depo20/services/saveAndAddSettlement';

import styles from './AddContainerByBLModalImportFCL.module.scss';
import { getContainersBL } from '../Depo20/services/getContainersBL';
import { getLiquidateContainerBl } from '../Depo20/services/getLiquidateContainerBl';

import InvoiceDetail from './InvoiceDetail';

type ModalProps = {
  setVisibility: (visibility: boolean) => void;
  visible: boolean;
  bodyOnSave: SaveSettlementValidated;
  oidLiquidacion: number;
  validRut: () => Promise<boolean>;
  rut: string;
  onLoadingChange?: (loading: boolean) => void;
};

const AddContainerByBLModalImportFCL: FC<ModalProps> = ({
  setVisibility,
  visible,
  bodyOnSave,
  oidLiquidacion,
  validRut,
  rut,
  onLoadingChange,
}) => {
  const { t } = useTranslation('importFCL');
  const { showToast } = useToastNotification();
  const { showMessage, elementRef, clearMessages } = useMessage();
  const {
    showMessage: showWarningMessage,
    elementRef: elementRefWarning,
    clearMessages: clearWarningMessage,
  } = useMessage();

  const showMessageWarning = useCallback(
    (message: string, title?: string) => {
      showWarningMessage({
        severity: 'warn',
        detail: '' ?? title,
        summary: message,
      });
    },
    [showWarningMessage]
  );

  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [formValue, setFormValue] = useState({
    bl: '',
    fechaRetiro: null,
    entregaDirecta: false,
  });
  const [selectedItems, setSelectedItems] = useState([] as DataBl[]);
  const [amountDetails, setAmountDetails] = useState<
    DataLiquidateContainerBL[]
  >([]);

  const toggleDialog = () => {
    setVisibility(!visible);
  };

  const { mutate: addContainersMutation, isPending: addingContainers } =
    useMutation({
      mutationFn: async (currentOidLiquidacion: number) => {
        const promises = selectedItems.map(async (item) => {
          try {
            const { oidVisita } = item || {};
            const { entregaDirecta, fechaRetiro } = formValue;
            const body = {
              oidVisita,
              entregaDirecta,
              fechaRetiro: fechaRetiro || '',
              oidLiquidacion: currentOidLiquidacion,
            };

            const res = await addContainerBlImport(body);
            return res;
          } catch (err) {
            const { message } = err as { message: string };
            const container = (item?.contenedor || '').replace(' ', '');
            const newMessage = `${container}-${message}`;

            throw new Error(newMessage);
          }
        });

        return Promise.allSettled(promises);
      },
      onSuccess: (res) => {
        clearMessages();
        clearWarningMessage();
        if (onLoadingChange) onLoadingChange(false);
        const count = res?.filter(
          ({ status }) => status === 'fulfilled'
        )?.length;
        if (count) {
          showToast({
            severity: 'success',
            detail: t('containersAdded', { count }),
          });
        }

        const errors = res?.filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected'
        );
        errors.forEach(({ reason }) => {
          const [container, message] = (reason?.message || '').split('-');
          showMessage({
            severity: 'error',
            summary: container,
            detail: message || t('errorAddingContainers'),
          });
        });
        queryClient.invalidateQueries({
          queryKey: ['getImportFCLTable'],
        });
        if (count === res.length) toggleDialog();
      },
      onError: (error) => {
        clearMessages();
        clearWarningMessage();
        showMessage({
          severity: 'error',
          detail: error?.message || t('errorAddingContainers'),
        });
      },
      onSettled: () => {
        setLoading(false);
      },
    });

  const {
    mutate: getContainersMutation,
    data: containers = [],
    isPending: containersLoading,
  } = useMutation({
    mutationFn: getContainersBL,
  });

  const processItems = async () => {
    clearMessages();
    clearWarningMessage();
    try {
      const validations = (selectedItems || []).map(async (item) => {
        const { entregaDirecta, fechaRetiro } = formValue;
        const { oidVisita, contenedor } = item || {};

        const body = {
          entregaDirecta,
          fechaRetiro: fechaRetiro || '',
          oidVisita,
          contenedor,
          rut,
        };

        return { response: { ok: true }, item, body };
      });

      const results = await Promise.all(validations);

      const newSelectedItems = results.map(({ item }) => item);
      setSelectedItems(newSelectedItems);

      const calculateAmounts = results.map(async ({ body }) => {
        const response = await getLiquidateContainerBl(body);
        return response;
      });

      const response = await Promise.allSettled(calculateAmounts);
      const newAmountDetails = response
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<DataLiquidateContainerBL> =>
            result.status === 'fulfilled'
        )
        .map(({ value }) => value);

      const invalidAmounts = response.filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      );
      invalidAmounts.forEach(({ reason }) => {
        showMessage({
          severity: 'error',
          detail: reason?.message || t('errorCalculatingAmount'),
        });
      });

      const containersWithWarnings = newAmountDetails.filter(
        (val) => val.warning
      );
      containersWithWarnings.forEach(({ warning, detalle }) => {
        if (warning) {
          showMessageWarning(
            `${detalle?.split(' - ')[0]} - ${warning} `,
            t('warning')
          );
        }
      });

      setAmountDetails(newAmountDetails);
    } catch (error) {
      showMessage({
        severity: 'error',
        detail: (error as { message: string })?.message as string,
      });
    } finally {
      setLoading(false);
    }
  };

  const { mutateAsync: saveData } = useMutation({
    mutationFn: saveAndAddSettlement,
  });

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(addingContainers);
    }
  }, [addingContainers, onLoadingChange]);

  const handleFieldChange = (
    value: string | Nullable<Date> | boolean,
    name: string
  ) => {
    setFormValue({
      ...formValue,
      [name]: value,
    });
  };

  const dialogHeader = (
    <div className="text-blue-800">
      <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
      {t('addContainerByBL')}
    </div>
  );

  const onGetContainersButtonClick = () => {
    const { bl } = formValue;
    getContainersMutation(bl);
  };

  useEffect(() => {
    setAmountDetails([]);
  }, [selectedItems]);

  const columns: ColumnProps[] = [
    {
      field: 'check',
      selectionMode: 'multiple',
      headerStyle: {
        borderTopLeftRadius: 16,
        width: '2rem',
        textAlign: 'center',
      },
    },
    {
      field: 'contenedor',
      header: t('containerNumber'),
      body: (rowData) => {
        const value = (rowData?.contenedor || '')?.replace(' ', '');
        return <b>{value}</b>;
      },
      headerStyle: { borderTopRightRadius: 16 },
    },
  ];

  const onCalculateButtonClick = () => {
    setLoading(true);
    processItems();
  };

  const onAddButtonClick = async () => {
    setLoading(true);
    const valid = await validRut();
    if (!valid || !containers) {
      setLoading(false);
      return;
    }
    if (!oidLiquidacion) {
      saveData(bodyOnSave);
      try {
        const settlementId = await saveData(bodyOnSave);
        addContainersMutation(settlementId);
        const newPath = `${location.pathname}?id=${settlementId}`;
        navigate(newPath, { replace: true });
        setLoading(false);
        showToast({
          severity: 'success',
          detail: t('settlementSaved'),
        });
      } catch (error) {
        setLoading(false);
        showToast({
          severity: 'error',
          detail: t('settlementNotSaved'),
        });
      }
      return;
    }

    addContainersMutation(oidLiquidacion);
  };

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{
        maxWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        minWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        position: 'relative',
      }}
      onHide={toggleDialog}
      modal
      footer={
        <div
          className={` p-2 md:p-4 ${styles.contianerButtons} md:border-top-1 border-400`}
        >
          <Messages ref={elementRef} />
          <Messages ref={elementRefWarning} />

          <div className={`w-full flex justify-content-end ${styles.buttons}`}>
            <Button text onClick={toggleDialog} style={{ color: '#170a33' }}>
              {t('cancel')}
            </Button>
            <Button
              onClick={onAddButtonClick}
              className={styles.containersButton}
              disabled={!amountDetails?.length || addingContainers}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t('add')}
            </Button>
          </div>
        </div>
      }
    >
      {loading && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="md:pb-4 px-2">
        <div className={`${styles.form} flex-column md:flex-row`}>
          <fieldset className="flex flex-column w-full align-items-start md:flex-row md:align-items-end">
            <InputTextField
              id="bl"
              className=" w-full mb-0 md:w-auto"
              name="bl"
              value={formValue.bl}
              onChange={(e) => handleFieldChange(e.target.value, 'bl')}
              label={t('bl')}
            />
            <Button
              onClick={onGetContainersButtonClick}
              className={` w-auto ${styles.containersButton}`}
              disabled={containersLoading}
            >
              {t('displayContainers')}
            </Button>
          </fieldset>
          <fieldset className="flex flex-column w-full align-items-start md:flex-row md:align-items-end">
            <InputDateField
              className="w-auto"
              id="fechaRetiro"
              name="fechaRetiro"
              value={formValue.fechaRetiro}
              onChange={(e) => handleFieldChange(e, 'fechaRetiro')}
              label={t('withdrawalDate')}
            />
          </fieldset>
        </div>
        <div className="flex-column md:flex-row mx-3">
          <InfoButton type="info" infoText={t('addContainerInfoMessage')} />
        </div>
      </div>
      <div
        className={`${styles.content} flex-column md:flex-row md:border-top-1 border-400`}
      >
        <div
          className={`border-400 md:border-right-1 md:pr-4 md:w-6 ${styles.containers}`}
        >
          <h2 className="text-blue-800">{t('containers')}</h2>
          <p>{t('containersMessage')}:</p>
          <Table
            columns={columns}
            data={containers}
            isLoading={containersLoading}
            className={styles.table}
            selectedItems={selectedItems}
            onSelectionChange={(e) => setSelectedItems(e.value)}
          />
        </div>
        <div className={`mt-3 md:pl-4 md:mt-0 ${styles.amount}`}>
          <header
            className={`flex flex-column md:flex-row md:align-items-center gap-3 ${styles.amountHeader}`}
          >
            <div>
              <h2 className="text-blue-800 mb-2">{t('amountTitle')}</h2>
              <p>{t('amountMessage')}</p>
            </div>
            <Button
              type="button"
              onClick={onCalculateButtonClick}
              style={{
                width: 'fit-content',
                textWrap: 'nowrap',
                padding: '0.7rem 1.3rem',
              }}
              disabled={!formValue.fechaRetiro || !selectedItems?.length}
            >
              <FontAwesomeIcon icon={faCalculator} className="mr-2" />
              {t('calculateAmount')}
            </Button>
          </header>
          {amountDetails?.map((item) => (
            <InvoiceDetail key={item.oid} data={item} />
          ))}
        </div>
      </div>
    </Dialog>
  );
};

export default AddContainerByBLModalImportFCL;
